import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";

/**
 * Ticket-level badges that surface AI assignment state. Reads the last entry
 * of `assignee_history` to decide what to show. Safe to render for every
 * ticket — returns null when the ticket wasn't touched by the AI agent.
 *
 * Accepts either a JSON string (what the API returns from MySQL longtext) or
 * an already-parsed array.
 *
 * Badge rules (derived purely from history — no bespoke ticket statuses):
 *   - "Assigned by AI"     → latest entry has assigned_by=AI_AGENT + employee_id
 *   - "Review Suggested"   → latest AI entry has review_flagged=true and
 *                            did assign an employee (medium-confidence path)
 *   - "Pending Assignment" → latest AI entry has flag='manual_review'
 *                            (no employee was assigned by the agent)
 */
interface HistoryEntry {
  employee_id?: number | null;
  assigned_at?: string;
  assigned_by?: string;
  confidence?: number | null;
  reason?: string;
  review_flagged?: boolean;
  flag?: string;
}

interface Props {
  assigneeHistory?: string | HistoryEntry[] | null;
  compact?: boolean;
}

function parseHistory(
  raw: string | HistoryEntry[] | null | undefined
): HistoryEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const AiAssignmentBadge: React.FC<Props> = ({ assigneeHistory, compact }) => {
  const history = parseHistory(assigneeHistory);
  const lastEntry =
    history.length > 0 ? history[history.length - 1] : undefined;

  const lastIsAi = lastEntry?.assigned_by === "AI_AGENT";
  if (!lastIsAi) return null;

  const hasEmployee = lastEntry?.employee_id != null;
  const isManualReview = lastEntry?.flag === "manual_review";
  const isReview = !!lastEntry?.review_flagged && hasEmployee;

  const confidencePct =
    lastEntry?.confidence != null
      ? Math.round(Number(lastEntry.confidence) * 100)
      : null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 0.75,
        alignItems: "center",
      }}
    >
      {hasEmployee && (
        <Tooltip
          title={lastEntry?.reason || "Assigned by AI agent"}
          placement="top"
        >
          <Chip
            size="small"
            color="info"
            icon={<SmartToyIcon fontSize="small" />}
            label={
              compact
                ? "AI"
                : `Assigned by AI${
                    confidencePct != null ? ` · ${confidencePct}%` : ""
                  }`
            }
          />
        </Tooltip>
      )}
      {isReview && (
        <Chip
          size="small"
          color="warning"
          label={compact ? "Review" : "Review Suggested"}
        />
      )}
      {isManualReview && (
        <Tooltip
          title={lastEntry?.reason || "AI handed ticket back for manual review"}
          placement="top"
        >
          <Chip
            size="small"
            color="error"
            label={compact ? "Pending" : "Pending Assignment"}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default AiAssignmentBadge;
